import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode, type MouseEvent as ReactMouseEvent, type PointerEvent as ReactPointerEvent } from 'react'
import { useAgentSession } from '../lib/voice-agent/AgentSessionContext'
import { useT } from '../i18n'
import { getMapPath } from '../config/project'
import { TSCHAUKO_POSTER_THUMB_URL } from '../lib/voice-agent/useTschaukoVideo'
import { useAuth } from '@/auth'

/**
 * A single footer-navigation entry.
 * Add or remove pages by editing FOOTER_NAV_ITEMS below — the bar renders
 * whatever is in that array, so extending it needs no other change.
 */
export interface FooterNavItem {
  /** Stable key for React + active-state matching. */
  key: string
  /** Visible label under the icon. */
  label: string
  /** Target path (plain navigation — the app routes on window.location). */
  href: string
  /** Inline icon, drawn with `stroke="currentColor"` so it inherits active/idle color. */
  icon: ReactNode
}

const MapIcon = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M9 4 3 6v14l6-2 6 2 6-2V4l-6 2-6-2Z" />
    <path d="M9 4v14" />
    <path d="M15 6v14" />
  </svg>
)

/**
 * Lupe statt Kompass (Alex 2026-08-05). Der Kompass las sich wie „Navigation"
 * — der Reiter führt aber zu Arten, Orten und Geschichten, die man SUCHT.
 * Der Name „Entdecken" bleibt; nur das Zeichen sagt jetzt, was man dort tut.
 */
const SearchIcon = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="11" cy="11" r="7" />
    <path d="m16.5 16.5 4 4" />
  </svg>
)

/**
 * Statt einer Sprechblase das GESICHT der Figur im Ring (Alex 2026-08-05:
 * „Chatbots klingen komisch"). Der Reiter heisst jetzt „Dr. Tschauko" — man
 * spricht mit einer Person, nicht mit einem Werkzeug.
 *
 * Das Bild ist ein Einzelbild des Idle-Clips (`variant=thumbnail&format=jpg`,
 * dieselbe Quelle wie das 64-px-Ambient-Bild), beschnitten auf den Kopf: das
 * Standbild zeigt die Figur in ganzer Groesse, im 22-px-Ring waere sie sonst
 * nicht zu erkennen. `object-position` holt den Kopf in den Ausschnitt.
 *
 * Der Ring erbt `currentColor`, damit er im aktiven Reiter mitgoldet.
 */
const TschaukoFaceIcon = (
  <span className="footer-nav__face" aria-hidden="true">
    <img src={TSCHAUKO_POSTER_THUMB_URL} alt="" loading="lazy" decoding="async" />
  </span>
)

/**
 * Default footer entries — the three mobile surfaces of the app:
 *   1. Karte (the map), 2. Entdecken (POI drop-up), 3. Guide (ChapGuide chat).
 * "discover" + "guide" are overlay/widget triggers, not real routes: the host
 * page intercepts them via `onSelect`. The `href` is only a graceful fallback
 * for pages that don't wire an interception (it routes back to the map).
 */
const TerminalIcon = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polyline points="4 17 10 11 4 5" />
    <line x1="12" y1="19" x2="20" y2="19" />
  </svg>
)

/** Debug-Tab nur im Dev-Build oder per explizitem `?debug=1` — der Tab stand
 *  sonst für Endnutzer im Live-Footer (Alex 27.07., Shot 115937). Der
 *  Query-Opt-in bleibt, damit am echten Gerät debuggt werden kann. */
export const DEBUG_TOOLS_ENABLED = import.meta.env?.DEV !== false
  || (typeof window !== 'undefined' && new URLSearchParams(window.location.search).has('debug'))

/**
 * Der Profil-Reiter (Alex 2026-08-05) wird ERST BEIM RENDERN erzeugt, nicht
 * hier: Er zeigt das Bild des angemeldeten Nutzers, und das steht beim Laden
 * des Moduls noch nicht fest. Ohne Anmeldung erscheint er gar nicht — ein
 * leerer Kreis waere eine Einladung ins Nichts.
 */
/**
 * Forschungstagebuch. Wohnte bis 2026-08-05 in NavigatePage — seit die
 * Profilseite denselben Footer zeigt, gehoert der Reiter dorthin, wo die
 * anderen stehen. Sonst hat dieselbe Leiste je nach Seite eine andere Form.
 */
export const RESEARCH_FOOTER_TAB: FooterNavItem = {
  key: 'research',
  label: 'Tagebuch',
  href: '#research',
  icon: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {/* Open book / journal */}
      <path d="M4 4.5A2.5 2.5 0 0 1 6.5 2H20v17H6.5A2.5 2.5 0 0 0 4 21.5V4.5Z" />
      <path d="M8 7h8M8 11h8M8 15h5" />
    </svg>
  ),
}

export const FOOTER_NAV_ITEMS: FooterNavItem[] = [
  { key: 'map', label: 'Karte', href: getMapPath(), icon: MapIcon },
  { key: 'discover', label: 'Entdecken', href: '/entdecken', icon: SearchIcon },
  { key: 'guide', label: 'Dr. Tschauko', href: getMapPath(), icon: TschaukoFaceIcon },
  // Öffnet das MobileDebugPanel (floating launcher überlappte den Footer — #220).
  ...(DEBUG_TOOLS_ENABLED ? [{ key: 'debug', label: 'Debug', href: '#', icon: TerminalIcon }] : []),
]

/**
 * Was ein Reiter tut, BEVOR die Seite ihr `onSelect` bekommt — auf jeder
 * Seite gleich, deshalb hier und nicht in der Seite.
 *
 * Eigene Funktion seit Post 5380: Eine native Fussleiste loest dieselben
 * Reiter ueber `nav.select` aus. Laege dieser Teil nur im Klick der
 * Web-Leiste, taete der native Tipp auf Dr. Tschauko und auf das Profil
 * etwas anderes als der Web-Tipp.
 *
 *   'done'     erledigt, die Seite wird nicht mehr gefragt
 *   'continue' erledigt, die Seite raeumt noch auf (`onSelect`)
 *   'none'     nichts Eigenes, die Seite entscheidet
 */
export function runFooterTabDefault(
  key: string,
  deps: {
    agent: Pick<ReturnType<typeof useAgentSession>, 'isActive' | 'restore' | 'controller'>
    signedIn: boolean
    startGoogleLogin: () => void
  },
): 'done' | 'continue' | 'none' {
  // Universal Chat-Bot tap-behaviour: restore minimized session OR open a
  // fresh one. Works across every page that mounts the FooterNav.
  if (key === 'guide') {
    if (deps.agent.isActive) deps.agent.restore()
    else deps.agent.controller.open()
    return 'continue'
  }
  // Profil ohne Anmeldung: der Tap IST der Anfang des Sign-ins
  // (Alex 2026-08-10) — der integrierte Google-Flow startet
  // sofort, statt erst ein leeres Profil-Overlay zu zeigen.
  // Nach dem Redirect zurück steht der Reiter mit Bild/Initialen.
  if (key === 'profile' && !deps.signedIn) {
    deps.startGoogleLogin()
    return 'done'
  }
  return 'none'
}

interface FooterNavProps {
  /** Override the entries; defaults to FOOTER_NAV_ITEMS. */
  items?: FooterNavItem[]
  /** Night sky-mode → dark glass surface. Light/day → light glass (default).
   *  Passed in because the bar lives outside the `.fv-page--<mode>` token scope. */
  dark?: boolean
  /** Intercept a tab tap. Return `true` to suppress navigation (e.g. open an
   *  in-page overlay instead of routing to the item's href). */
  onSelect?: (key: string, e: ReactMouseEvent) => boolean | void
  /** Force a tab active regardless of the current path — used when a tab maps
   *  to an overlay (no URL change), e.g. the Entdecken drop-up over the map. */
  activeKey?: string
}

/**
 * Mobile-only bottom tab bar. Hidden on desktop via CSS (`.footer-nav` is
 * `display:none` above the mobile breakpoint). Active item is derived from the
 * current path (or an explicit `activeKey`), so it stays correct without any
 * router wiring.
 */
export default function FooterNav({ items = FOOTER_NAV_ITEMS, dark = false, onSelect, activeKey }: FooterNavProps) {
  const path = typeof window !== 'undefined' ? window.location.pathname : ''
  // Tschauko voice-agent state — when the session is alive but the
  // overlay is hidden (minimized), the Chat-Bot tab pulses to remind
  // the user that the bot is still active in the background. Tapping
  // it restores the overlay (handled below).
  const agent = useAgentSession()
  const guideMinimizedActive = agent.isActive && agent.isMinimized
  /* Der Puls sagt „die Sitzung laeuft" — sprechen kann man aber erst, wenn die
   * Figur selbst einmal geklungen hat. Bis dahin laeuft der Verbindungsaufbau
   * und ein Druck aufs Mikrofon verpufft (Alex 2026-08-12). Deshalb traegt die
   * GESTE den Riegel, nicht die Anzeige: Der Puls bleibt der Hinweis auf die
   * laufende Sitzung, das lange Druecken kommt erst dazu, wenn es auch wirkt. */
  const sprechbereit = guideMinimizedActive && agent.hasSpoken
  const { user, startGoogleLogin } = useAuth()

  // Localise the tab labels via the shared i18n map. Consumers pass items
  // with stable keys (map/discover/guide/research/debug); the label field
  // remains a fallback for custom entries a page might add on top.
  const t = useT()
  const labelByKey: Record<string, string> = {
    map: t.footerNav.map,
    discover: t.footerNav.discover,
    guide: t.footerNav.chatBot,
    research: t.footerNav.diary,
    debug: t.footerNav.debug,
  }
  const localizedItems = useMemo(
    () => items.map((it) => ({ ...it, label: labelByKey[it.key] ?? it.label })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [items, t],
  )

  /** Profil-Reiter neben dem Tagebuch — IMMER sichtbar (Alex 2026-08-10,
   *  Aufnahme 118680: „von Haus aus eingeblendet"). Angemeldet: Google-Bild
   *  bzw. Initialen im Ring. Abgemeldet: neutrale Person-Silhouette — der
   *  Tap startet dann direkt den integrierten Google-Sign-in (siehe
   *  onClick unten), statt ein leeres Profil zu zeigen. */
  const profileItem: FooterNavItem = useMemo(() => {
    if (!user) {
      return {
        key: 'profile',
        label: t.footerNav.profile,
        href: '/profil',
        icon: (
          <span className="footer-nav__face footer-nav__face--anon" aria-label={t.footerNav.profileSignIn}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
              <circle cx="12" cy="7" r="4" />
            </svg>
          </span>
        ),
      }
    }
    const name = `${user.first_name ?? ''} ${user.last_name ?? ''}`.trim()
    const kuerzel = ((user.first_name ?? '').charAt(0) + (user.last_name ?? '').charAt(0)).toUpperCase()
      || (user.email ?? '?').charAt(0).toUpperCase()
    return {
      key: 'profile',
      label: user.first_name || t.footerNav.profile,
      href: '/profil',
      icon: (
        <span className="footer-nav__face" aria-label={name || t.footerNav.profile}>
          {user.picture
            ? <img src={user.picture} alt="" referrerPolicy="no-referrer" loading="lazy" decoding="async" />
            : <span className="footer-nav__face-initials" aria-hidden="true">{kuerzel}</span>}
        </span>
      ),
    }
  /* `t` gehoert in die Abhaengigkeiten: Ohne sie friert die Beschriftung auf
     der Sprache ein, die beim ersten Aufbau galt — der Reiter blieb im
     englischen Betrieb „Profil", obwohl `footerNav.profile` sehr wohl
     „Profile" sagt (Alex 2026-08-12). Die uebrigen Reiter fielen nicht auf,
     weil sie ihre Beschriftung bei jedem Rendern frisch aus `t` holen. */
  }, [user, t])

  /* Langer Druck auf den Kopf = Sprechen, kurzer Tipp = Gespräch öffnen.
   *
   * Läuft der Agent und ist zugeklappt, pulsiert der Kopf in der Leiste. Genau
   * dann ist er die nächstliegende Sprechtaste — die Ganzkörperfigur trägt PTT
   * längst, aber sie steht nur da, wenn die Karte frei ist (Alex 2026-08-11).
   *
   * Bewusst über Zeiger-Ereignisse: Ein `click` käme erst NACH dem Loslassen,
   * also zu spät zum Zuhören. Und der Klick, der dem langen Druck folgt, wird
   * unterdrückt — sonst spricht man erst und bekommt danach ungefragt den
   * Dialog. */
  const HALTEN_MS = 350
  const halteUhr = useRef<number | null>(null)
  const sprichtGerade = useRef(false)
  const klickSchlucken = useRef(false)
  const [mikroOffen, setMikroOffen] = useState(false)

  const halteEnde = useCallback(() => {
    if (halteUhr.current != null) { window.clearTimeout(halteUhr.current); halteUhr.current = null }
    if (!sprichtGerade.current) return
    sprichtGerade.current = false
    klickSchlucken.current = true
    setMikroOffen(false)
    agent.controller.setMicActive(false)
  }, [agent])

  const halteBeginn = useCallback(() => {
    if (halteUhr.current != null) window.clearTimeout(halteUhr.current)
    klickSchlucken.current = false
    halteUhr.current = window.setTimeout(() => {
      halteUhr.current = null
      sprichtGerade.current = true
      setMikroOffen(true)
      // Spürbare Rückmeldung: Der Kopf sieht beim Halten fast gleich aus,
      // und ohne Signal weiß niemand, ob das Mikrofon schon offen ist.
      navigator.vibrate?.(15)
      agent.controller.setMicActive(true)
    }, HALTEN_MS)
  }, [agent])

  useEffect(() => () => { if (halteUhr.current != null) window.clearTimeout(halteUhr.current) }, [])

  const shownItems = [...localizedItems, profileItem]

  return (
    <nav className={`footer-nav${dark ? ' footer-nav--dark' : ''}`} aria-label={t.nav.home}>
      {shownItems.map((item) => {
        const active = activeKey ? activeKey === item.key : path === item.href
        const isGuide = item.key === 'guide'
        const pulsing = isGuide && guideMinimizedActive
        const haltenMoeglich = isGuide && sprechbereit
        /**
         * NUR die Mitte traegt ein Gesicht — und zwar gross (Alex 2026-08-10,
         * Aufnahme 118858: „in der Mitte wurde der Button fuer Dr. Ciocco
         * groesser gemacht, das gefaellt mir … kannst du den Footer auch so
         * aufbereiten").
         *
         * Das ist die uebliche Form fuer eine Leiste mit einer Hauptsache:
         * vier gleichrangige Reiter mit Symbol und Namen, dazwischen die eine
         * Handlung als erhoehter Kreis. Sie sagt ohne Worte, welcher Knopf der
         * wichtige ist — was die frueher gleich grossen Kreise (2026-08-05)
         * nicht konnten.
         *
         * Das Profil faellt damit in die Reihe zurueck: Symbol + Name wie
         * Route, Entdecken und Tagebuch. Zwei gleich grosse Portraetkreise
         * nebeneinander hatten keine Rangordnung — mit dem grossen in der
         * Mitte haette der kleine daneben nur noch verwirrt.
         */
        const portrait = item.key === 'guide'
        return (
          <a
            key={item.key}
            href={item.href}
            className={`footer-nav__item${active ? ' footer-nav__item--active' : ''}${pulsing ? ' footer-nav__item--pulsing' : ''}${portrait ? ' footer-nav__item--portrait footer-nav__item--hero' : ''}`}
            aria-current={active ? 'page' : undefined}
            {...(haltenMoeglich ? {
              onPointerDown: (e: ReactPointerEvent) => { e.preventDefault(); halteBeginn() },
              onPointerUp: halteEnde,
              onPointerCancel: halteEnde,
              onPointerLeave: halteEnde,
              // Ohne das öffnet iOS beim langen Druck die Linkvorschau.
              onContextMenu: (e: ReactMouseEvent) => e.preventDefault(),
              'data-mic': mikroOffen ? 'true' : 'false',
            } : {})}
            /* Ohne sichtbare Beschriftung traegt der Reiter seinen Namen
               nur noch hier — sonst hiesse er fuer Screenreader „Link". */
            aria-label={portrait ? item.label : undefined}
            onClick={(e) => {
              // Der Klick, der einem langen Druck folgt, gehoert nicht zur
              // Geste — sonst spricht man erst und bekommt danach ungefragt
              // den Dialog.
              if (klickSchlucken.current) {
                klickSchlucken.current = false
                e.preventDefault()
                return
              }
              // Universal Chat-Bot tap-behaviour: restore minimized
              // session OR open a fresh one. Works across every page
              // that mounts the FooterNav (not only NavigatePage).
              if (item.key === 'debug') {
                e.preventDefault()
                window.dispatchEvent(new CustomEvent('toggle-debug-panel'))
                onSelect?.(item.key, e)
                return
              }
              const eigen = runFooterTabDefault(item.key, { agent, signedIn: !!user, startGoogleLogin })
              if (eigen === 'done') {
                e.preventDefault()
                return
              }
              if (eigen === 'continue') {
                e.preventDefault()
                // Still let the host onSelect run for any page-local
                // bookkeeping (e.g. close other overlays).
                onSelect?.(item.key, e)
                return
              }
              if (onSelect?.(item.key, e)) e.preventDefault()
            }}
          >
            <span className="footer-nav__icon">{item.icon}</span>
            {!portrait && <span className="footer-nav__label">{item.label}</span>}
          </a>
        )
      })}
    </nav>
  )
}
